import { JsonPipe, NgClass, UpperCasePipe } from '@angular/common';
import { Component, signal, inject, computed, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UiModule } from '@components/ui/ui-module';
import { Card, CardCateg, CardSet, Feature, Lang, MarketPlace, Rarity, ThirdParty} from '@models/card.model';
import { Card as CardService } from '@services/card.service';
import { Global } from '@services/global.service';
import { Router } from '@services/route.service';
import { forkJoin, map, switchMap } from 'rxjs';

@Component({
  selector: 'lgb-card-form',
  imports: [
    UiModule,
    RouterModule,
    UpperCasePipe,
    NgClass,
    FormsModule
  ],
  templateUrl: './card-form.html',
  styleUrl: './card-form.css',
})
export class CardForm {
  readonly cardS=inject(CardService);
  readonly global=inject(Global);
  readonly route=inject(ActivatedRoute);
  readonly router=inject(Router);

  //data
  readonly markets=signal<MarketPlace[]>([]);
  readonly categs=signal<CardCateg[]>([]);
  readonly cardSets=signal<CardSet[]>([]);
  readonly rarities=signal<Rarity[]>([]);
  readonly card=signal<Card>(null);
  readonly name=computed(() => this.cardS.getNameFeat(this.card(), this.lang()));
  readonly images=signal<Map<string, File>>(new Map());

  //ui
  readonly loading=signal<boolean>(true);
  readonly lang=signal<string>('fr');
  readonly msg=signal<string>(null);
  readonly error=signal<string>(null);
  readonly isNew=computed(() => this.card().ident==null);
  readonly previews=signal<Map<string, string>>(new Map());

  constructor(){
    //init data
    const card=new Card();
    card.initNames();
    this.card.set(card);
    effect(() => {
      const previews: Map<string, string>=new Map();
      this.images().forEach((img, lang) => {
        const reader=new FileReader();
        reader.onload=() => {
          this.previews.update(val => {
            const next=new Map(val);
            next.set(lang, reader.result as string);
            return next;
          });
        }
        reader.readAsDataURL(img);
      });
    });

    //recup datas
    this.route.paramMap.pipe(
      switchMap(params => {
        let obs: any={
          init: this.global.init(),
          categs: this.global.getCategs(),
          cardSets: this.cardS.getCardSets().pipe(
            map(sets => sets.reverse())
          )
        };

        if(params.get('id'))
          obs.card=this.cardS.getCard(params.get('id'));

        return forkJoin(obs);
      })
    )
    .subscribe((datas: any) => {
      if(datas.card)
        this.card.set(datas.card);
      this.categs.set(datas.categs);
      this.markets.set(datas.init.marketplaces);
      this.rarities.set(datas.init.rarities.sort((a: Rarity, b: Rarity) => a.index-b.index));
      this.cardSets.set(datas.cardSets);
      this.loading.set(false);
    });
  }

  setImage(event: Event, lang: string){
    const input=event.target as HTMLInputElement;

    if(!input.files || input.files.length===0)
      this.images.update(val => {
        const next=new Map(val);
        next.delete(lang);
        return next;
      });
    else{
      this.images.update(val => {
        const next=new Map(val);
        next.set(lang, input.files[0]);
        return next;
      });
    }
  }

  getImage(lang: string){
    let img: string=null;
    if(this.previews().get(lang))
      img=this.previews().get(lang);
    else if(this.card())
      img=this.cardS.getImage(this.card()?.img, lang);

    return img;
  }

  getLangs(){
    return Lang.getLangs();
  }

  getFeatures(){
    return this.card()?.features.filter(f => f.lang==this.lang() && f.name!='name');
  }

  getNameFeat(feat: Feature){
    let result: string=null;
    if(feat.name.indexOf('attack-name')>-1)
      result="Nom de l'attaque";
    else if(feat.name.indexOf('attack-text')>-1)
      result="Effet de l'attaque";
    else if(feat.name.indexOf('trainer-text')>-1)
      result="Effet du dresseur";
    else if(feat.name.indexOf('abilities')>-1)
      result="Habilité";
    return result;
  }

  getMarketPlace(market: string){
    return this.markets().find(m => m.ref==market);
  }

  addParty(market: string){
    let party: ThirdParty=new ThirdParty();
    party.card=this.card().ident;
    party.marketplace=market;

    this.card().thirdParties.push(party);
  }

  getMarketDispo(){
    return this.markets().filter(m => !this.card().thirdParties.find(t => t.marketplace==m.ref));
  }

  setCard(){
    this.loading.set(true);
    this.cardS.setCard(this.card(), this.images()).subscribe({
      next: card => {
        this.msg.set(((this.isNew())? 'Carte ajoutée.' : 'Carte modifiée.'));
        this.card.set(card);
        this.loading.set(false);
      },
      error: e => {
        this.error.set(e);
        this.loading.set(false)
      }
    });
  }

  setName(name: string){
    if(this.name())
      this.name().text=name;
  }

  cardSetName(cardSet: CardSet){
    return this.cardS.getCardSetName(cardSet, this.lang());
  }

  rmParty(market: string){
    this.card().thirdParties.splice(this.card().thirdParties.findIndex(p => p.marketplace==market), 1);
  }

  rmCard(){
    if(confirm('Voulez-vous supprimer cette carte ?')){
      this.loading.set(true);
      this.cardS.rmCard(this.card().ident).subscribe({
        next: () => this.router.navigate('admin-card-list'),
        error: e => {
          this.error.set(e);
          this.loading.set(false);
        }
      });
    }
  }
}
